import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  DateRangePicker,
  Form,
  FormField,
  FormItem,
  FormMessage,
} from "@sentricmusic/ui";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import AccountFilter from "./_accountFilter";
import SongFilter from "./_songsFilter";
import WriterFilter from "./_writersFilter";
import { useUIStateStore } from "@/store/uiStateStore";

const filterSchema = z.object({
  dateRange: z.object({ from: z.date(), to: z.date() }).nullable().refine(val => val?.from && val?.to, {
    message: "Please select a date range before searching",
  }),
});

function RoyaltiesFilter({ defaultAccount, onSearch, initialDateRange }) {
  const { t } = useTranslation("other");
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const [accounts, setAccounts] = useState(defaultAccount ? [defaultAccount] : []);
  const [writers, setWriters] = useState([]);
  const [songs, setSongs] = useState([]);

  const form = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues: { dateRange: initialDateRange ?? null },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  // Re-seed when the global account changes
  useEffect(() => {
    setAccounts(defaultAccount ? [defaultAccount] : []);
    setWriters([]);
    setSongs([]);
    form.setValue("dateRange", initialDateRange ?? null, { shouldValidate: false });
  }, [defaultAccount, initialDateRange, form]);

  const handleAccountChange = useCallback((values) => {
    setAccounts(values);
    setWriters([]);
    setSongs([]);
  }, []);

  const handleWriterChange = useCallback((values) => {
    setWriters(values);
    setSongs([]);
  }, []);

  const handleSongChange = useCallback((values) => {
    setSongs(values);
  }, []);

  const handleReset = useCallback(() => {
    setAccounts(defaultAccount ? [defaultAccount] : []);
    setWriters([]);
    setSongs([]);
    form.reset({ dateRange: initialDateRange ?? null });
  }, [defaultAccount, initialDateRange, form]);

  const canSearch = accounts.length > 0;

  const handleSearch = form.handleSubmit((values) => {
    onSearch({
      accounts,
      writers,
      songs,
      dateFrom: values.dateRange.from,
      dateTo: values.dateRange.to,
    });
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSearch} noValidate>
        <div className="mb-4 flex flex-wrap gap-4">
          <div className="flex w-full flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <AccountFilter
                values={accounts}
                onValuesChange={handleAccountChange}
              />
            </div>
            <div className="flex-1 min-w-64">
              <WriterFilter
                accounts={accounts}
                values={writers}
                onValuesChange={handleWriterChange}
              />
            </div>
          </div>

          <div className="flex w-full flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <SongFilter
                accounts={accounts}
                writers={writers}
                values={songs}
                onValuesChange={handleSongChange}
              />
            </div>

            <div className="flex flex-1 flex-col items-end gap-1 min-w-64">
              <FormField
                control={form.control}
                name="dateRange"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <DateRangePicker
                      multiYear
                      fromYear={2012}
                      toYear={new Date().getFullYear()}
                      label={t("other:date-range", "Date Range")}
                      initialRange={field.value ?? undefined}
                      onSelect={range => field.onChange(range ?? null)}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex gap-2 mt-4 justify-between sm:justify-end w-full">
                <Button
                  type="submit"
                  variant={isTunecore ? "secondary" : "primary"}
                  label={t("other:search", "Search")}
                  disabled={!canSearch}
                />
                <Button
                  type="button"
                  variant={isTunecore ? "primary" : "secondary"}
                  label={t("other:reset", "Reset")}
                  onClick={handleReset}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}

export default RoyaltiesFilter;
